import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  Check,
  Upload,
  Trash2,
  Loader2,
  X,
  Package,
  MapPin,
  ArrowLeft,
  Send,
  Phone,
  Share2,
  Globe,
  ChevronDown,
  Image as ImageIcon
} from "lucide-react";
import { BsWhatsapp, BsGlobe2 } from "react-icons/bs";
import { toast } from "sonner";
import { Button } from "../../Components/UI/button";
import { Input } from "../../Components/UI/input";
import { Textarea } from "../../Components/UI/textarea";
import { Label } from "../../Components/UI/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../Components/UI/dialog";
import { Drawer, DrawerContent } from "../../Components/UI/drawer";
import { SpeechControls } from "../../Components/Common/SpeechControls";
import MobileInput from "../../Components/Common/MobileInput";
import { POST_CATEGORIES } from "./PostCategoriesConfig";
import { setPostAPI } from "./FeedAPI";
import externalAPI from "../../Connect/ExternalAPI";
import { segments, industries } from "../../Components/Enums/DataEnums";
import { resolveCatalogImageUrl } from "../Settings/WebStoreConfig/WebStoreCatalog";
import { getAuthData } from "../../Utils/AuthUtils";
import { getCurrentUser, getUploadUrl, openInExternalBrowser } from "../../Utils/Utilities";
import { getCustomerInfo, saveLeadInfo } from "../../Utils/Storage";
import apiService from "../../Services/APIService";
import { COMMUNITY_ALLOW_MULTIPLE_IMAGES } from "../../Utils/FeatureFlags";
import { Avatar } from "./CommunityShared";
import useIsMobile from "../../Hooks/useIsMobile";

const MAX_PHOTOS_COUNT = 5;

// ============================================================================
// 1. PURE HELPERS
// ============================================================================
function copyToClipboard(text, successMessage) {
  if (navigator?.clipboard?.writeText) {
    navigator.clipboard.writeText(text)
      .then(() => toast.success(successMessage))
      .catch(() => fallbackCopy(text, successMessage));
  } else {
    fallbackCopy(text, successMessage);
  }
}

function fallbackCopy(text, successMessage) {
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
    toast.success(successMessage);
  } catch (e) {
    toast.error("Failed to copy to clipboard");
  }
}

// ============================================================================
// 2. REUSABLE MICRO-COMPONENTS
// ============================================================================

/** Contact detail inputs (Company, Name, Mobile, Email) */
function InquiryContactFields({
  form,
  onChange,
  className = "",
  inputHeight = "h-10 md:h-11",
  rounded = "rounded-md",
  hidden = false
}) {
  if (hidden) return null;

  return (
    <div className={`space-y-3.5 md:space-y-4 ${className}`}>
      {/* Row 1: Company Name & Your Name */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 md:gap-4">
        <div className="space-y-1 md:space-y-1.5 text-left drawer-field scroll-mt-3">
          <Label className="text-sm font-medium text-[var(--text-color)]">Company Name</Label>
          <Input
            type="text"
            placeholder="e.g. Acme Corp"
            value={form.company}
            onChange={(e) => onChange("company", e.target.value)}
            className={`${inputHeight} text-sm text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] ${rounded}`}
          />
        </div>

        <div className="space-y-1 md:space-y-1.5 text-left drawer-field scroll-mt-3">
          <Label className="text-sm font-medium text-[var(--text-color)]">
            Your Name <span className="text-red-500 font-bold">*</span>
          </Label>
          <Input
            type="text"
            required
            placeholder="e.g. John Doe"
            value={form.name}
            onChange={(e) => onChange("name", e.target.value)}
            className={`${inputHeight} text-sm text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] ${rounded}`}
          />
        </div>
      </div>

      {/* Row 2: Mobile Number & Email Address */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 md:gap-4">
        <div className="space-y-1 md:space-y-1.5 text-left drawer-field scroll-mt-3">
          <Label className="text-sm font-medium text-[var(--text-color)]">
            Mobile Number <span className="text-red-500 font-bold">*</span>
          </Label>
          <MobileInput
            value={form.mobile}
            onChange={(e) => onChange("mobile", e.target.value.replace(/[^0-9]/g, ""))}
            selectedCountryCode={form.cc || "91"}
            onCountryCodeChange={(newCC) => onChange("cc", newCC)}
            placeholder="e.g. 9876543210"
            className="w-full"
            containerClassName={`${inputHeight} ${rounded}`}
            inputClassName="text-sm"
          />
        </div>

        <div className="space-y-1 md:space-y-1.5 text-left drawer-field scroll-mt-3">
          <Label className="text-sm font-medium text-[var(--text-color)]">Email Address</Label>
          <Input
            type="email"
            placeholder="e.g. john@example.com"
            value={form.email}
            onChange={(e) => onChange("email", e.target.value)}
            className={`${inputHeight} text-sm text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] ${rounded}`}
          />
        </div>
      </div>
    </div>
  );
}

/** Fullscreen image zoom portal */
function OfferingZoomModal({ imageUrl, onClose }) {
  useEffect(() => {
    if (!imageUrl) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [imageUrl, onClose]);

  if (!imageUrl) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100005] bg-black/85 backdrop-blur-xs flex items-center justify-center p-4 select-none animate-in fade-in duration-200 pointer-events-auto"
      data-radix-portal=""
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={onClose}
    >
      <button
        type="button"
        className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white/90 hover:text-white bg-black/60 hover:bg-black/80 p-2.5 rounded-full transition-all cursor-pointer z-[100006] shadow-lg border border-white/20 flex items-center justify-center pointer-events-auto"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        title="Close fullscreen"
      >
        <X className="h-6 w-6" />
        <span className="sr-only">Close fullscreen</span>
      </button>
      <div
        className="relative flex items-center justify-center cursor-default pointer-events-auto overflow-hidden rounded-2xl border border-white/20 shadow-2xl bg-white"
        style={{
          width: "85vmin",
          height: "85vmin",
          maxWidth: "650px",
          maxHeight: "650px",
          aspectRatio: "1 / 1"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={imageUrl}
          alt="Offering Zoom Preview"
          className="w-full h-full object-cover"
        />
      </div>
    </div>,
    document.body
  );
}

// ============================================================================
// 3. PromoteBusiness (formerly CommunityComposer)
// ============================================================================
export function PromoteBusiness({ onPostCreated }) {
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [category, setCategory] = useState(POST_CATEGORIES[0].id);
  const [files, setFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [publicComments, setPublicComments] = useState(true);
  const [loading, setLoading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const fileInputRef = useRef(null);

  const authData = getAuthData();
  const currentUser = getCurrentUser() || {};
  const companyName = authData?.AgencyName || currentUser?.AgencyName || "Your business";
  const agencyConfig = authData?.AgencyConfig || {};
  const allowMulti = COMMUNITY_ALLOW_MULTIPLE_IMAGES && Boolean(agencyConfig?.AllowMultiCommunityPostPhotos);

  const dynamicPlaceholders = useMemo(() => {
    const selected = POST_CATEGORIES.find((c) => c.id === category) || POST_CATEGORIES[0];
    return {
      title: selected.titleTpl.replace(/{company}/g, companyName),
      details: selected.detailsTpl.replace(/{company}/g, companyName)
    };
  }, [category, companyName]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    const remainingSlots = allowMulti ? MAX_PHOTOS_COUNT - files.length : 1;
    if (remainingSlots <= 0) {
      toast.error(`You can only upload up to ${MAX_PHOTOS_COUNT} images.`);
      return;
    }

    const validFiles = [];
    for (const file of selectedFiles.slice(0, remainingSlots)) {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image file.`);
        continue;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 2MB limit.`);
        continue;
      }
      validFiles.push(file);
    }

    if (!validFiles.length) return;

    if (allowMulti) {
      setFiles((prev) => [...prev, ...validFiles]);
      const newUrls = validFiles.map((f) => URL.createObjectURL(f));
      setPreviewUrls((prev) => [...prev, ...newUrls]);
    } else {
      setFiles(validFiles.slice(0, 1));
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
      setPreviewUrls([URL.createObjectURL(validFiles[0])]);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveFile = (index) => {
    if (previewUrls[index]) {
      URL.revokeObjectURL(previewUrls[index]);
    }
    setFiles((prev) => prev.filter((_, idx) => idx !== index));
    setPreviewUrls((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSubmitPost = async () => {
    if (!title.trim()) return toast.error("Please enter a title for your post.");
    if (title.length < 5) return toast.error("Title must be at least 5 characters long.");
    if (!details.trim()) return toast.error("Please enter details for your post.");

    setLoading(true);
    try {
      const uploadedPaths = [];
      for (let i = 0; i < files.length; i++) {
        const uploadResult = await apiService.uploadFile(files[i], "Feed");
        if (uploadResult?.error) {
          throw new Error(uploadResult.message || `Failed to upload image ${i + 1}.`);
        }
        const path = uploadResult?.data?.relativePath || "";
        if (path) uploadedPaths.push(path);
      }

      await setPostAPI({
        postID: 0,
        title: title.trim(),
        detail: details.trim(),
        postTypeID: category,
        audienceTypeID: publicComments ? "1" : "2",
        photos: uploadedPaths,
        isDeleted: 0
      });

      toast.success("Post created successfully!");
      resetForm();
      if (onPostCreated) onPostCreated();
    } catch (error) {
      console.error("Error saving post:", error);
      toast.error(error?.message || "This post could not be saved.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTitle("");
    setDetails("");
    setFiles([]);
    previewUrls.forEach((u) => URL.revokeObjectURL(u));
    setPreviewUrls([]);
    setPublicComments(true);
    setCategory(POST_CATEGORIES[0].id);
  };

  const reorderFiles = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    setFiles((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
    setPreviewUrls((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
  };

  const logoSrc = authData?.Logo
    ? authData.Logo.startsWith("http")
      ? authData.Logo
      : getUploadUrl(`Uploads/${authData.Logo}`)
    : "";

  return (
    <div className="w-full h-full bg-sahi-white border border-sahi-border-color rounded-[7px] shadow-xs overflow-hidden flex flex-col transition-all">
      {/* Header Card / Banner */}
      <div className="p-3.5 sm:p-4 border-b border-sahi-border-color/70 bg-white rounded-t-[7px] flex items-center justify-between gap-3 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar src={logoSrc} name={companyName} size={42} className="shadow-2xs shrink-0" />
          <div className="min-w-0">
            <h2 className="text-sm sm:text-base font-bold text-text-color truncate leading-tight">
              Promote Your Business
            </h2>
            <p className="text-xs text-sub-text-color font-medium truncate mt-0.5">
              Grow your network and shine in the Community
            </p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 sm:p-5 overflow-y-auto custom-scroll flex-1">
        <div className="space-y-4">
          {/* Post Category Selection */}
          <div className="space-y-1.5">
            <Label>What are you sharing?</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {POST_CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                const isSelected = category === cat.id;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setCategory(cat.id)}
                    className={`flex items-center justify-center gap-2 p-2.5 sahi-radius border text-center text-xs font-semibold transition-all cursor-pointer bg-sahi-white ${isSelected
                      ? "border-sahi-green text-sahi-green shadow-xs"
                      : " text-sub-text-color border-sahi-border-color hover:border-gray-400 hover:bg-gray-50"
                      }`}
                  >
                    <Icon className={`h-4 w-4 shrink-0 ${isSelected ? "text-sahi-green" : "text-sub-text-color"}`} />
                    <span className="truncate">{cat.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Title Field */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="inlinePostTitle" required>Title</Label>
              {title.length >= 145 && (
                <span className="text-[11px] text-sub-text-color">{title.length}/150</span>
              )}
            </div>
            <Input
              id="inlinePostTitle"
              name="postTitle"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 150))}
              placeholder={dynamicPlaceholders.title}
              className="h-11 text-sm sahi-input"
              maxLength={150}
            />
          </div>

          {/* Details Field */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="inlinePostDetails" required>Details</Label>
              {details.length >= 495 && (
                <span className="text-[11px] text-sub-text-color">{details.length}/500</span>
              )}
            </div>
            <Textarea
              id="inlinePostDetails"
              name="postDetails"
              value={details}
              onChange={(e) => setDetails(e.target.value.slice(0, 500))}
              placeholder={dynamicPlaceholders.details}
              rows={5}
              className="min-h-24 text-sm sahi-input"
              maxLength={500}
            />
          </div>

          {/* Images Upload Area */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>{allowMulti ? "Attach Images" : "Attach Image"}</Label>
              {allowMulti && (
                <span className="text-[11px] font-medium text-sub-text-color">
                  {files.length > 1 ? "Drag to reorder • " : ""}
                  {files.length}/{MAX_PHOTOS_COUNT}
                </span>
              )}
            </div>

            {previewUrls.length > 0 ? (
              <div className="space-y-2">
                <div className={allowMulti ? "grid grid-cols-4 sm:grid-cols-5 gap-2 sm:gap-2.5" : "flex gap-3"}>
                  {previewUrls.map((url, idx) => (
                    <div
                      key={idx}
                      data-thumb-index={idx}
                      draggable={allowMulti}
                      onDragStart={allowMulti ? (e) => { setDraggedIndex(idx); e.dataTransfer.effectAllowed = "move"; } : undefined}
                      onDragOver={allowMulti ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dragOverIndex !== idx) setDragOverIndex(idx); } : undefined}
                      onDragLeave={allowMulti ? () => setDragOverIndex(null) : undefined}
                      onDrop={allowMulti ? (e) => { e.preventDefault(); if (draggedIndex !== null) reorderFiles(draggedIndex, idx); setDraggedIndex(null); setDragOverIndex(null); } : undefined}
                      onDragEnd={allowMulti ? () => { setDraggedIndex(null); setDragOverIndex(null); } : undefined}
                      className={`relative group rounded-[7px] border overflow-hidden aspect-square flex items-center justify-center shadow-2xs transition-all select-none ${allowMulti ? "w-full cursor-grab active:cursor-grabbing" : "w-28 sm:w-32"} ${allowMulti && draggedIndex === idx
                        ? "opacity-35 scale-95 border-dashed border-sahi-primary-color bg-gray-100"
                        : allowMulti && dragOverIndex === idx
                          ? "ring-2 ring-sahi-primary-color ring-offset-2 scale-105 border-sahi-primary-color bg-sahi-disable"
                          : "border-sahi-border-color bg-sahi-disable hover:shadow-sm"
                        }`}
                      title={allowMulti ? "Drag to reorder" : undefined}
                    >
                      <img
                        src={url}
                        alt={allowMulti ? `Preview ${idx + 1}` : "Preview"}
                        className="w-full h-full object-cover pointer-events-none select-none"
                        draggable={false}
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(idx);
                        }}
                        className="absolute top-1 right-1 sm:top-1.5 sm:right-1.5 p-1 bg-sahi-red/90 text-white rounded-full hover:bg-sahi-red shadow-sm transition-colors cursor-pointer z-10"
                        title="Remove image"
                      >
                        <Trash2 className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                      </button>
                      {allowMulti && (
                        <div className="absolute bottom-1 left-1 sm:bottom-1.5 sm:left-1.5 px-1.5 py-0.5 rounded bg-black/65 backdrop-blur-xs text-[10px] text-white font-semibold pointer-events-none z-10">
                          {idx + 1}
                        </div>
                      )}
                    </div>
                  ))}

                  {allowMulti && files.length < MAX_PHOTOS_COUNT && (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="relative group rounded-[7px] bg-zinc-50 hover:bg-zinc-100 flex flex-col items-center justify-center gap-1 cursor-pointer transition-colors w-full aspect-square"
                    >
                      <svg className="absolute inset-0 w-full h-full pointer-events-none rounded-[7px]" xmlns="http://www.w3.org/2000/svg">
                        <rect
                          x="1"
                          y="1"
                          width="calc(100% - 2px)"
                          height="calc(100% - 2px)"
                          rx="6"
                          ry="6"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeDasharray="4 3"
                          className="text-zinc-300 group-hover:text-zinc-400 transition-colors"
                        />
                      </svg>
                      <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-white flex items-center justify-center shadow-2xs group-hover:scale-105 transition-transform z-1">
                        <Upload className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-zinc-500" />
                      </div>
                      <span className="text-[11px] sm:text-xs font-semibold text-text-color z-1">Add More</span>
                    </div>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple={allowMulti}
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="relative w-full p-5 py-8 rounded-[7px] bg-white hover:bg-zinc-50 flex flex-col items-center justify-center gap-1 cursor-pointer transition-all shadow-xs group text-center"
              >
                <svg className="absolute inset-0 w-full h-full pointer-events-none rounded-[7px]" xmlns="http://www.w3.org/2000/svg">
                  <rect
                    x="1.5"
                    y="1.5"
                    width="calc(100% - 3px)"
                    height="calc(100% - 3px)"
                    rx="6"
                    ry="6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeDasharray="5 4"
                    className="text-zinc-300 group-hover:text-zinc-400 transition-colors"
                  />
                </svg>
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-xs group-hover:scale-105 transition-transform z-1 mb-1">
                  <Upload className="h-4 w-4 text-zinc-500" />
                </div>
                <p className="text-xs font-semibold text-text-color z-1">
                  {allowMulti ? "Click to upload images (up to 5)" : "Click to upload image"}
                </p>
                <p className="text-[11px] text-sub-text-color mt-0.5 z-1">
                  {allowMulti ? "PNG, JPG & JPEG up to 2MB each" : "PNG, JPG & JPEG up to 2MB"}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple={allowMulti}
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
            )}
          </div>

          {/* Comments Visibility Checkbox */}
          <div className="flex items-center gap-2 whitespace-nowrap flex-shrink-0">
            <input
              type="checkbox"
              id="inlineAllowComments"
              checked={publicComments}
              onChange={(e) => setPublicComments(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-sahi-primary-color focus:ring-sahi-primary-color cursor-pointer accent-sahi-primary-color flex-shrink-0"
            />
            <Label htmlFor="inlineAllowComments" className="cursor-pointer mb-0">
              Allow others to see comments
            </Label>
          </div>
        </div>
      </div>

      {/* Action Buttons - Fixed at bottom */}
      <div className="px-4 py-3 sm:px-5 sm:py-3.5 border-t border-sahi-border-color/60 bg-sahi-white rounded-b-[7px] shrink-0">
        <Button
          onClick={handleSubmitPost}
          disabled={loading}
          className="sahi-primary-button w-full text-xs sm:text-sm font-semibold cursor-pointer justify-center rounded-[7px]"
        >
          <Check className="h-4 w-4" />
          {loading ? "Sharing..." : "Share Post"}
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// 4. WebstorePreview (formerly CompactCatalogPreview)
// ============================================================================
export function WebstorePreview({ agency, onClose, isDrawer = false }) {
  const [loading, setLoading] = useState(true);
  const [agencyProfile, setAgencyProfile] = useState(null);
  const [catalogItems, setCatalogItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const agencyID = Number(agency?.agencyID || agency?.AgencyID || 0);

  useEffect(() => {
    let isMounted = true;
    const fetchCatalog = async () => {
      if (!agencyID) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await externalAPI.getAgencyVitals(agencyID);
        if (!isMounted) return;

        let updtPkg = res?.updatePackage;
        if (typeof updtPkg === "string") {
          try {
            updtPkg = JSON.parse(updtPkg);
          } catch (e) {
            console.error("Error parsing updatePackage:", e);
          }
        }

        const table1Data = updtPkg?.Table1?.[0] || {};
        setAgencyProfile(table1Data);

        const rawCatalog =
          updtPkg?.Catalog ||
          updtPkg?.catalog ||
          table1Data?.Catalog ||
          table1Data?.catalog ||
          [];

        let parsed = [];
        if (Array.isArray(rawCatalog)) {
          parsed = rawCatalog;
        } else if (typeof rawCatalog === "string") {
          try {
            const json = JSON.parse(rawCatalog);
            parsed = Array.isArray(json) ? json : [];
          } catch (e) {
            parsed = [];
          }
        }

        const activeProducts = parsed.filter((item) => !item.IsD);
        setCatalogItems(activeProducts);
      } catch (err) {
        console.error("Failed to fetch catalog preview:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchCatalog();
    return () => {
      isMounted = false;
    };
  }, [agencyID]);

  const companyName =
    agencyProfile?.AgencyName ||
    agency?.agencyName ||
    agency?.CreatedAgencyName ||
    "Business Catalog";

  const rawLogo = agencyProfile?.Logo || agency?.agencyLogo || agency?.CreatedAgencyLogo || "";
  const logoUrl = rawLogo ? (rawLogo.startsWith("http") ? rawLogo : getUploadUrl(`Uploads/${rawLogo}`)) : "";

  const [isInquiryOpen, setIsInquiryOpen] = useState(false);
  const hasSetDefaultInquiryOpen = useRef(false);
  const inquirySectionRef = useRef(null);

  useEffect(() => {
    hasSetDefaultInquiryOpen.current = false;
  }, [agencyID]);

  useEffect(() => {
    if (!loading && !hasSetDefaultInquiryOpen.current) {
      if (catalogItems.length === 0) {
        setIsInquiryOpen(true);
      }
      hasSetDefaultInquiryOpen.current = true;
    }
  }, [loading, catalogItems.length]);

  const city = (agencyProfile?.City || agency?.city || "").trim();
  const state = (agencyProfile?.State || agency?.state || "").trim();
  const location = [city, state].filter(Boolean).join(", ");
  const mobile = agencyProfile?.Mobile || agencyProfile?.AdminMobile || agency?.mobile || "";

  // Industry & Segment Resolution
  const indId = agencyProfile?.IndustryID || agency?.industryID || agency?.IndustryID;
  const segId = agencyProfile?.SegmentID || agency?.segmentID || agency?.SegmentID;
  const foundInd = industries.find((i) => i.IndustryID === indId)?.Industry || agencyProfile?.Industry || agency?.industry || "";
  const foundSeg = segments.find((s) => s.SegmentID === segId)?.Segment || agencyProfile?.Segment || agency?.segment || "";
  const displayIndustry = foundInd;
  const displaySegment = foundSeg;

  // Tagline calculation
  const rawTagline = (agencyProfile?.Tagline || agencyProfile?.Description || agency?.tagline || "").trim();
  const displayTagline = rawTagline || (displaySegment && displayIndustry
    ? `Supplier of ${displaySegment} (${displayIndustry})${location ? ` based in ${location}` : ""}`
    : location ? `Based in ${location}` : "Supplier of Quality Products & Services");

  const handleOpenStore = () => {
    if (agencyID) {
      const webstoreUrl = `${window.location.origin}/web/store?m=${agencyID}`;
      openInExternalBrowser(webstoreUrl);
    }
  };

  const handleWhatsAppInquiry = (product = null) => {
    if (!mobile) {
      handleOpenStore();
      return;
    }
    const cleanNumber = mobile.replace(/[^0-9]/g, "");
    const fullNumber = cleanNumber.startsWith("91") ? cleanNumber : `91${cleanNumber}`;
    const textMsg = product
      ? `Hi, I saw your product "${product.PNm || product.name}" on SahiClub Community and would like more details.`
      : `Hi, I saw your catalog on SahiClub Community and would like to connect.`;
    window.open(`https://wa.me/${fullNumber}?text=${encodeURIComponent(textMsg)}`, "_blank");
  };

  const isMobile = useIsMobile();

  const handleCall = (e) => {
    if (!mobile) {
      toast.info("Phone number not provided");
      return;
    }
    const isDesktop = !isMobile || (typeof window !== "undefined" && window.innerWidth >= 1024);
    if (isDesktop) {
      if (e) e.preventDefault();
      copyToClipboard(mobile, "Phone number copied to clipboard!");
    } else {
      window.location.href = `tel:${mobile}`;
    }
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/web/store?m=${agencyID}`;
    const isDesktop = !isMobile || (typeof window !== "undefined" && window.innerWidth >= 1024);

    if (isDesktop) {
      copyToClipboard(shareUrl, "Webstore link copied to clipboard!");
      return;
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: companyName,
          text: `Check out ${companyName}'s webstore on SahiClub`,
          url: shareUrl,
        });
        return;
      } catch (err) {
        if (err.name === "AbortError") return;
      }
    }
    copyToClipboard(shareUrl, "Webstore link copied to clipboard!");
  };

  // Offering Inquiry Modal States & Zoom
  const [zoomImageUrl, setZoomImageUrl] = useState(null);
  const [submittingOfferingInquiry, setSubmittingOfferingInquiry] = useState(false);
  const [mobileInquiryStep, setMobileInquiryStep] = useState(1);
  const [offeringInquiryForm, setOfferingInquiryForm] = useState(() => {
    const customer = getCustomerInfo(agencyID);
    return {
      company: customer?.company || "",
      name: customer?.name || "",
      mobile: customer?.mobile || "",
      cc: customer?.cc || "91",
      email: customer?.email || "",
      needs: "",
    };
  });

  const updateFormField = (field, value) => {
    setOfferingInquiryForm((prev) => ({ ...prev, [field]: value }));
  };

  const hasKnownContact = Boolean(
    offeringInquiryForm.name?.trim() && offeringInquiryForm.mobile?.trim()
  );

  const handleScrollToInquiry = () => {
    setIsInquiryOpen(true);
    const scrollAndFocus = () => {
      if (inquirySectionRef.current) {
        inquirySectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      setTimeout(() => {
        const input = document.getElementById("compact-preview-needs-input");
        if (input) input.focus();
      }, 250);
    };

    if (!isInquiryOpen) {
      setTimeout(scrollAndFocus, 50);
    } else {
      scrollAndFocus();
    }
  };

  const resetOfferingModalState = () => {
    setSelectedProduct(null);
    setMobileInquiryStep(1);
    const customer = getCustomerInfo(agencyID);
    setOfferingInquiryForm({
      company: customer?.company || "",
      name: customer?.name || "",
      mobile: customer?.mobile || "",
      cc: customer?.cc || "91",
      email: customer?.email || "",
      needs: "",
    });
  };

  const handleDrawerFieldFocus = (e) => {
    const field = e.target.closest(".drawer-field");
    const container = field?.closest(".drawer-form-scroll");
    if (field && container) {
      const scrollFieldToTop = () => {
        const containerRect = container.getBoundingClientRect();
        const fieldRect = field.getBoundingClientRect();
        const maxScroll = container.scrollHeight - container.clientHeight;
        const targetScrollTop = container.scrollTop + (fieldRect.top - containerRect.top) - 8;
        container.scrollTo({
          top: Math.min(maxScroll, Math.max(0, targetScrollTop)),
          behavior: "smooth",
        });
      };
      setTimeout(scrollFieldToTop, 100);
      setTimeout(scrollFieldToTop, 320);
    }
  };

  const handleOfferingInquirySubmit = async (e) => {
    if (e) e.preventDefault();
    if (!offeringInquiryForm.needs.trim()) {
      toast.error("Please enter requirement details.");
      return;
    }
    if (!offeringInquiryForm.name.trim()) {
      toast.error("Please enter your name.");
      return;
    }
    if (!offeringInquiryForm.mobile.trim()) {
      toast.error("Please enter mobile number.");
      return;
    }
    const mobileDigits = offeringInquiryForm.mobile.trim().replace(/\D/g, "");
    const ccDigits = (offeringInquiryForm.cc || "91").replace(/\D/g, "") || "91";
    if (ccDigits === "91" && mobileDigits.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    } else if (ccDigits !== "91" && (mobileDigits.length < 5 || mobileDigits.length > 15)) {
      toast.error("Please enter a valid mobile number.");
      return;
    }
    if (offeringInquiryForm.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(offeringInquiryForm.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }

    const offeringName = selectedProduct?.PNm || selectedProduct?.name || selectedProduct?.Title || "";

    setSubmittingOfferingInquiry(true);
    try {
      const response = await externalAPI.sendWebstoreInquiry({
        agencyID: agencyID,
        company: offeringInquiryForm.company.trim() || "-",
        name: offeringInquiryForm.name.trim(),
        mobile: mobileDigits,
        cc: ccDigits,
        email: offeringInquiryForm.email.trim(),
        product: offeringName,
        needs: offeringInquiryForm.needs.trim(),
        source: "Community",
        agencyName: companyName || "Business Catalog",
        ownerMobile: mobile || "",
        ownerName: companyName || "",
        ownerCC: "91",
      });

      if (response?.error === false || response?.code === 200 || response?.status === "SUCCESS") {
        toast.success("Inquiry submitted! We will contact you soon.");
        saveLeadInfo({
          name: offeringInquiryForm.name.trim(),
          mobile: mobileDigits,
          cc: ccDigits,
          company: offeringInquiryForm.company.trim(),
          email: offeringInquiryForm.email.trim(),
        });
        resetOfferingModalState();
      } else {
        toast.error(response?.message || response?.status || "Failed to submit inquiry. Please try again.");
      }
    } catch (err) {
      console.error("Error submitting offering inquiry:", err);
      toast.error("Error submitting inquiry. Please try again later.");
    } finally {
      setSubmittingOfferingInquiry(false);
    }
  };

  return (
    <div className={`w-full h-full bg-sahi-white rounded-[7px] overflow-hidden flex flex-col transition-all relative ${isDrawer ? "shadow-none" : "border border-sahi-border-color shadow-xs"}`}>
      {/* Header Card / Banner */}
      <div className="p-4 sm:p-5 border-b border-[var(--border)] bg-white flex items-center justify-between gap-4 shrink-0 shadow-[0_1px_3px_rgba(0,0,0,0.03)]">
        <div className="flex items-center gap-3.5 sm:gap-4 min-w-0 flex-1">
          {logoUrl ? (
            <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-18 lg:h-18 rounded-xl sm:rounded-2xl border border-[var(--border)] bg-white overflow-hidden flex items-center justify-center shrink-0">
              <img
                src={logoUrl}
                alt={companyName || "Business Logo"}
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = "none";
                  if (e.target.nextElementSibling) {
                    e.target.nextElementSibling.style.display = "flex";
                  }
                }}
              />
              <div className="hidden w-full h-full">
                <Avatar
                  name={companyName}
                  size={64}
                  className="w-full h-full !rounded-xl sm:!rounded-2xl !text-base !font-bold"
                />
              </div>
            </div>
          ) : (
            <Avatar
              name={companyName}
              size={64}
              className="w-14 h-14 sm:w-16 sm:h-16 lg:w-18 lg:h-18 shrink-0 aspect-square !rounded-xl sm:!rounded-2xl !text-base !font-bold border border-[var(--border)]"
            />
          )}
          <div className="flex flex-col justify-center items-start text-left min-w-0 flex-1">
            <h1
              className="text-lg sm:text-xl font-bold text-[var(--text-color)] tracking-tight leading-snug truncate w-full"
              title={companyName}
            >
              {companyName}
            </h1>
            {displayTagline && (
              <p
                className="text-xs sm:text-sm text-[var(--sub-text-color)] font-normal leading-normal mt-0.5 truncate w-full"
                title={displayTagline}
              >
                {displayTagline}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area - Scrollable */}
      <div className="p-4 sm:p-5 flex-1 overflow-y-auto custom-scroll space-y-4 bg-white">
        {/* Catalog Section */}
        {loading ? (
          <div className="space-y-3">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-[var(--text-color)]">Catalog</h2>
              <p className="text-xs sm:text-sm text-[var(--sub-text-color)] mt-0.5">Click on an offering to inquire.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-5 !mt-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex flex-col animate-pulse">
                  <div className="aspect-square w-full rounded-xl bg-zinc-100 mb-2 border border-[var(--border)]" />
                  <div className="h-3 bg-zinc-200 rounded w-3/4 mx-auto" />
                </div>
              ))}
            </div>
          </div>
        ) : catalogItems.length > 0 ? (
          <div className="space-y-3">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-[var(--text-color)]">Catalog</h2>
              <p className="text-xs sm:text-sm text-[var(--sub-text-color)] mt-0.5">Click on an offering to inquire.</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-5 !mt-3">
              {catalogItems.map((item, idx) => {
                const name = item.PNm || item.name || item.Title || "Product";
                const rawImg = item.PImg || item.pImg || item.Img || item.img || item.imageUrl || "";
                const imgUrl = resolveCatalogImageUrl(rawImg);

                return (
                  <div
                    key={item.PID || idx}
                    className="flex flex-col cursor-pointer group"
                    onClick={() => {
                      setSelectedProduct(item);
                      setMobileInquiryStep(1);
                      const customer = getCustomerInfo(agencyID);
                      setOfferingInquiryForm((prev) => ({
                        company: prev.company || customer?.company || "",
                        name: prev.name || customer?.name || "",
                        mobile: prev.mobile || customer?.mobile || "",
                        cc: prev.cc || customer?.cc || "91",
                        email: prev.email || customer?.email || "",
                        needs: "",
                      }));
                    }}
                  >
                    <div
                      className="relative aspect-square w-full rounded-xl overflow-hidden bg-white flex items-center justify-center border border-[var(--border)] group-hover:border-[var(--sahi-green)]/40 shadow-[0_0_14px_rgba(0,0,0,0.12)] hover:shadow-[0_0_18px_rgba(0,0,0,0.18)] transition-all"
                      title="Click to inquire"
                    >
                      {imgUrl ? (
                        <img
                          src={imgUrl}
                          alt={name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.style.display = "none";
                            if (e.target.nextElementSibling) {
                              e.target.nextElementSibling.style.display = "flex";
                            }
                          }}
                        />
                      ) : null}
                      <div className={`${imgUrl ? "hidden" : "flex"} absolute inset-0 items-center justify-center bg-zinc-100 text-zinc-400`}>
                        <Package className="w-8 h-8 opacity-40" />
                      </div>
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors pointer-events-none" />
                    </div>

                    <div className="pt-2 pb-1 px-1 flex flex-col items-center text-center w-full">
                      <h3
                        className="font-semibold text-xs sm:text-sm text-[var(--text-color)] group-hover:text-[var(--sahi-green)] transition-colors truncate w-full text-center"
                        title={name}
                      >
                        {name}
                      </h3>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* Business Background */}
        {(displayIndustry || displaySegment || location) && (
          <div className="text-left pt-1">
            <h3 className="text-sm sm:text-lg font-bold text-[var(--text-color)] mb-3 sm:mb-3.5">
              Business Background
            </h3>
            <div className="space-y-3.5">
              {(displayIndustry || displaySegment) && (
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#0b5d1e15] text-[var(--sahi-green)] flex items-center justify-center shrink-0">
                    <Globe className="w-5 h-5 text-[var(--sahi-green)]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-[var(--sub-text-color)] font-medium">Industry & Segment</div>
                    <div className="text-xs sm:text-sm font-semibold text-[var(--text-color)] truncate mt-0.5" title={[displayIndustry, displaySegment].filter(Boolean).join(" > ")}>
                      {[displayIndustry, displaySegment].filter(Boolean).join(" > ")}
                    </div>
                  </div>
                </div>
              )}

              {location && (
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#0b5d1e15] text-[var(--sahi-green)] flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-[var(--sahi-green)]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-[var(--sub-text-color)] font-medium">Location</div>
                    <div className="text-xs sm:text-sm font-semibold text-[var(--text-color)] truncate mt-0.5" title={location}>
                      {location}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Embedded Inquiry Form (Collapsible) */}
        <div ref={inquirySectionRef} className="text-left pt-1 scroll-mt-4">
          <div
            onClick={() => setIsInquiryOpen((prev) => !prev)}
            className="flex items-center justify-between cursor-pointer select-none group"
          >
            <div>
              <h3 className="text-sm sm:text-lg font-bold text-[var(--text-color)] group-hover:text-[var(--sahi-green)] transition-colors">
                Inquiry
              </h3>
              <p className="text-xs text-[var(--sub-text-color)] mt-0.5">
                Enter your requirement and we will get back to you.
              </p>
            </div>
            <ChevronDown className={`h-5 w-5 text-[var(--sub-text-color)] transition-transform duration-200 shrink-0 ml-2 ${isInquiryOpen ? "rotate-180" : ""}`} />
          </div>

          {isInquiryOpen && (
            <form noValidate onSubmit={handleOfferingInquirySubmit} className="space-y-3.5 md:space-y-4 mt-3">
              <div className="space-y-1 md:space-y-1.5 text-left">
                <Label className="text-sm font-medium text-[var(--text-color)]">
                  Requirement <span className="text-red-500 font-bold">*</span>
                </Label>
                <div className="relative">
                  <Textarea
                    id="compact-preview-needs-input"
                    rows={3}
                    required
                    placeholder="e.g. quantity, specifications..."
                    value={offeringInquiryForm.needs}
                    onChange={(e) => updateFormField("needs", e.target.value)}
                    className="text-sm text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-md min-h-[90px] sm:min-h-[120px] resize-y pr-14"
                  />
                  <SpeechControls
                    targetId="compact-preview-needs-input"
                    value={offeringInquiryForm.needs}
                    onChange={(val) => updateFormField("needs", val)}
                    isMobile={isMobile}
                    className="absolute right-2 top-2"
                  />
                </div>
              </div>

              {/* Prefilled contact fields hidden if name & mobile exist */}
              <InquiryContactFields
                form={offeringInquiryForm}
                onChange={updateFormField}
                hidden={hasKnownContact}
              />
            </form>
          )}
        </div>
      </div>

      {/* Pinned Bottom Footer Action Bar */}
      <div className="px-4 py-3 sm:px-5 sm:py-3.5 border-t border-sahi-border-color/60 bg-sahi-white shrink-0">
        <div className="w-full flex items-center justify-between gap-2 sm:gap-3">
          {/* Left: Back button + Action icons */}
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
            {onClose ? (
              <button
                type="button"
                onClick={onClose}
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-[7px] hover:bg-zinc-200 text-zinc-700 flex items-center justify-center transition-all cursor-pointer shadow-2xs active:scale-95 shrink-0"
                title="Back"
              >
                <ArrowLeft className="w-4 h-4 text-zinc-700" />
              </button>
            ) : null}

            <button
              type="button"
              onClick={handleOpenStore}
              className="h-9 sm:h-10 px-2.5 sm:px-3 rounded-[7px] bg-gray-200 hover:bg-gray-300/80 text-zinc-700 flex items-center gap-1.5 transition-all cursor-pointer active:scale-95 shadow-2xs shrink-0"
              title="Visit Web Store"
            >
              <BsGlobe2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-zinc-700 shrink-0" />
              <span className="text-xs font-medium whitespace-nowrap">Visit Web Store</span>
            </button>

            {mobile && (
              <button
                type="button"
                onClick={handleCall}
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-[7px] bg-sahi-blue-light hover:opacity-90 text-sahi-dark-blue flex items-center justify-center transition-all cursor-pointer shadow-2xs active:scale-95 shrink-0"
                title={`Call ${mobile}`}
              >
                <Phone className="w-[18px] h-[18px] text-sahi-dark-blue" />
              </button>
            )}

            <button
              type="button"
              onClick={() => handleWhatsAppInquiry()}
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-[7px] bg-[var(--sahi-green-light)] hover:opacity-90 text-[var(--sahi-green)] flex items-center justify-center transition-all cursor-pointer shadow-2xs active:scale-95 shrink-0"
              title="Chat on WhatsApp"
            >
              <BsWhatsapp className="w-[18px] h-[18px] text-[var(--sahi-green)]" />
            </button>

            <button
              type="button"
              onClick={handleShare}
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-[7px] bg-gray-100 hover:bg-gray-200 text-zinc-700 flex items-center justify-center transition-all cursor-pointer shadow-2xs active:scale-95 shrink-0"
              title="Share Webstore link"
            >
              <Share2 className="w-[18px] h-[18px] text-zinc-700" />
            </button>
          </div>

          {/* Right: Inquire / Submit Button */}
          <div className="flex items-center shrink-0">
            <Button
              type="button"
              variant="sahiGreen"
              disabled={submittingOfferingInquiry}
              onClick={(e) => {
                if (offeringInquiryForm.needs.trim()) {
                  handleOfferingInquirySubmit(e);
                } else {
                  handleScrollToInquiry();
                }
              }}
              className="h-9 sm:h-10 px-3.5 sm:px-4 rounded-[7px] text-xs font-semibold cursor-pointer flex items-center gap-1.5 shadow-2xs shrink-0 !transform-none hover:!transform-none hover:!translate-y-0 active:!translate-y-0 active:!scale-100 transition-colors"
              title={offeringInquiryForm.needs.trim() ? "Submit Inquiry" : "Send Inquiry"}
            >
              {submittingOfferingInquiry ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="text-sm font-medium">{offeringInquiryForm.needs.trim() ? "Submit" : "Inquire"}</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Offering Zoom Modal */}
      <OfferingZoomModal
        imageUrl={zoomImageUrl}
        onClose={() => setZoomImageUrl(null)}
      />

      {/* Offering Inquiry Modal (Drawer on Mobile, Dialog on Desktop) */}
      {selectedProduct && (() => {
        const rawImg = selectedProduct.PImg || selectedProduct.Img || selectedProduct.img || selectedProduct.imageUrl || selectedProduct.ImgURL || selectedProduct.image || "";
        const detailImgUrl = rawImg ? resolveCatalogImageUrl(rawImg) : "";
        const productName = selectedProduct.PNm || selectedProduct.name || selectedProduct.Title || "Offering";
        const offeringDescription = selectedProduct.PTxt || selectedProduct.pTxt || selectedProduct.description || selectedProduct.Desc || selectedProduct.Description || selectedProduct.PDesc || "";
        const rate = parseFloat(selectedProduct.Rate || selectedProduct.price) || 0;

        if (isMobile) {
          return (
            <Drawer
              open={Boolean(selectedProduct) && !zoomImageUrl}
              onOpenChange={(isOpen) => { if (!isOpen) resetOfferingModalState(); }}
              shouldScaleBackground={false}
              repositionInputs={false}
            >
              <DrawerContent
                onFocusCapture={handleDrawerFieldFocus}
                className="p-0 [&>div:first-child]:hidden !top-auto !bottom-0 !h-auto max-h-[90dvh] rounded-t-[22px] border-t border-[var(--border)] z-[10002] overflow-hidden bg-background flex flex-col"
                style={{ '--sahi-primary-color': '#0b5d1e' }}
              >
                {/* Header */}
                <div className="flex items-center px-4 h-14 shrink-0 bg-background rounded-t-[22px]">
                  <div className="flex gap-3 items-center min-w-0 flex-1">
                    <button
                      type="button"
                      tabIndex={-1}
                      className="p-0 w-auto h-auto flex-shrink-0 flex justify-center text-gray-500 hover:text-gray-800 cursor-pointer"
                      onClick={() => {
                        if (mobileInquiryStep === 2) setMobileInquiryStep(1);
                        else resetOfferingModalState();
                      }}
                      title="Back"
                    >
                      <ArrowLeft className="h-6 w-6" />
                      <span className="sr-only">Back</span>
                    </button>
                    <p className="text-lg font-semibold text-gray-900 truncate">
                      {mobileInquiryStep === 2 ? "Contact Details" : "Inquiry"}
                    </p>
                  </div>
                </div>

                {/* Step 1: Needs */}
                {mobileInquiryStep === 1 && (
                  <form
                    noValidate
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!offeringInquiryForm.needs.trim()) {
                        toast.error("Please enter your requirement.");
                        const needsInput = document.getElementById("mobile-drawer-catalog-needs-input");
                        if (needsInput) needsInput.focus();
                        return;
                      }
                      setMobileInquiryStep(2);
                    }}
                    className="flex-1 min-h-0 flex flex-col overflow-hidden"
                  >
                    <div className="overflow-y-auto custom-scroll flex-1 min-h-0 p-4 pb-10 space-y-4 drawer-form-scroll scroll-pt-3 scroll-pb-10">
                      <div className="flex flex-col items-start text-left space-y-2 w-full">
                        <div className="w-full relative rounded-xl sm:rounded-2xl overflow-hidden border border-[var(--border)] bg-zinc-50 h-48 sm:h-56 flex items-center justify-center">
                          {detailImgUrl ? (
                            <img
                              src={detailImgUrl}
                              alt={productName}
                              className="w-full h-full object-contain cursor-zoom-in group/zoom bg-zinc-50"
                              onClick={() => { if (detailImgUrl) setZoomImageUrl(detailImgUrl); }}
                              title="Click to view full screen"
                              onError={(e) => {
                                e.target.style.display = "none";
                                if (e.target.nextElementSibling) e.target.nextElementSibling.style.display = "flex";
                              }}
                            />
                          ) : null}
                          <div className={`${detailImgUrl ? "hidden" : "flex"} w-full h-full bg-zinc-100 items-center justify-center text-zinc-400`}>
                            <ImageIcon className="w-12 h-12 opacity-40" />
                          </div>
                        </div>

                        <div className="w-full text-left space-y-0.5 pt-1">
                          <div className="flex items-start justify-between gap-3">
                            <h3 className="text-lg sm:text-xl font-bold text-[var(--text-color)] text-left break-words leading-snug" title={productName}>
                              {productName}
                            </h3>
                            {rate > 0 && (
                              <div className="shrink-0 pt-0.5 text-left">
                                <span className="inline-flex items-center text-xs font-bold text-[var(--sahi-green)] bg-[var(--sahi-green-light)] px-2.5 py-1 rounded-md border border-[var(--sahi-green)]/15">
                                  ₹{rate.toLocaleString()}
                                </span>
                              </div>
                            )}
                          </div>
                          {offeringDescription ? (
                            <div className="text-sm text-[var(--sub-text-color)] leading-relaxed pt-0.5 max-h-24 overflow-y-auto custom-scroll break-words whitespace-pre-line text-left w-full">
                              {offeringDescription}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <hr className="border-t border-[var(--border)] !mt-3" />

                      <div className="space-y-1.5 text-left !mt-3 drawer-field scroll-mt-3">
                        <Label className="text-sm font-medium text-[var(--text-color)]">
                          Requirement <span className="text-red-500 font-bold">*</span>
                        </Label>
                        <div className="relative">
                          <Textarea
                            id="mobile-drawer-catalog-needs-input"
                            rows={3}
                            required
                            placeholder="e.g. quantity, specifications..."
                            value={offeringInquiryForm.needs}
                            onChange={(e) => updateFormField("needs", e.target.value)}
                            className="text-sm sm:text-base text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg min-h-[84px] resize-y pr-14"
                          />
                          <SpeechControls
                            targetId="mobile-drawer-catalog-needs-input"
                            value={offeringInquiryForm.needs}
                            onChange={(val) => updateFormField("needs", val)}
                            isMobile={isMobile}
                            className="absolute right-2 top-2"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="px-4 py-2.5 sm:py-3 flex items-center bg-background shrink-0 border-t border-[var(--border)]/40">
                      <Button
                        type="submit"
                        variant="sahiGreen"
                        className="w-full h-11 font-semibold text-sm flex items-center justify-center gap-1.5 cursor-pointer rounded-lg shadow-xs"
                      >
                        <Send className="h-4 w-4" /> Submit Inquiry
                      </Button>
                    </div>
                  </form>
                )}

                {/* Step 2: Contact Details */}
                {mobileInquiryStep === 2 && (
                  <form
                    noValidate
                    onSubmit={handleOfferingInquirySubmit}
                    className="flex-1 min-h-0 flex flex-col overflow-hidden"
                  >
                    <div className="overflow-y-auto custom-scroll flex-1 min-h-0 p-4 pb-10 space-y-4 drawer-form-scroll scroll-pt-3 scroll-pb-10">
                      <InquiryContactFields
                        form={offeringInquiryForm}
                        onChange={updateFormField}
                        inputHeight="h-11"
                        rounded="rounded-lg"
                      />
                    </div>

                    <div className="px-4 py-2.5 sm:py-3 flex items-center bg-background shrink-0 border-t border-[var(--border)]/40">
                      <Button
                        type="submit"
                        variant="sahiGreen"
                        disabled={submittingOfferingInquiry}
                        className="w-full h-11 font-semibold text-sm flex items-center justify-center gap-1.5 cursor-pointer rounded-lg shadow-xs"
                      >
                        {submittingOfferingInquiry ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" /> Submitting...
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4" /> Submit Inquiry
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                )}
              </DrawerContent>
            </Drawer>
          );
        }

        // Desktop Dialog
        return (
          <Dialog
            open={Boolean(selectedProduct)}
            onOpenChange={(isOpen) => { if (!isOpen) resetOfferingModalState(); }}
          >
            <DialogContent
              className="w-full max-w-2xl max-h-[90vh] flex flex-col p-0 overflow-hidden rounded-xl border border-[var(--border)] bg-background shadow-2xl outline-none focus:outline-none ring-0"
              style={{ '--sahi-primary-color': '#0b5d1e' }}
            >
              <DialogHeader className="gap-0 p-4 shrink-0 flex flex-row items-center justify-between bg-background">
                <DialogTitle className="text-xl font-bold text-[var(--text-color)] truncate pr-2 text-left">
                  Inquiry
                </DialogTitle>
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={resetOfferingModalState}
                  className="rounded-lg p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors cursor-pointer shrink-0"
                  title="Close"
                >
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close</span>
                </button>
              </DialogHeader>

              <form
                noValidate
                onSubmit={handleOfferingInquirySubmit}
                className="flex-1 flex flex-col overflow-hidden"
              >
                <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[calc(90vh-130px)] custom-scroll">
                  {/* Selected Offering Summary */}
                  <div className="flex items-start gap-4 max-w-full">
                    {detailImgUrl ? (
                      <img
                        src={detailImgUrl}
                        alt={productName}
                        className="w-24 h-24 rounded-xl object-cover shrink-0 cursor-zoom-in group/zoom border border-[var(--border)]"
                        onClick={() => { if (detailImgUrl) setZoomImageUrl(detailImgUrl); }}
                        title="Click to view full screen"
                        onError={(e) => {
                          e.target.style.display = "none";
                          if (e.target.nextElementSibling) e.target.nextElementSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div className={`${detailImgUrl ? "hidden" : "flex"} w-24 h-24 rounded-xl bg-zinc-100 items-center justify-center text-zinc-400 shrink-0 border border-[var(--border)]`}>
                      <ImageIcon className="w-8 h-8 opacity-40" />
                    </div>
                    <div className="flex-1 min-w-0 text-left pr-2 flex flex-col justify-start">
                      <div className="flex items-start justify-between gap-3">
                        <div className="text-2xl font-bold text-[var(--text-color)] break-words leading-tight" title={productName}>
                          {productName}
                        </div>
                        {rate > 0 && (
                          <div className="shrink-0 pt-0.5">
                            <span className="inline-flex items-center text-xs font-bold text-[var(--sahi-green)] bg-[var(--sahi-green-light)] px-2.5 py-1 rounded-md border border-[var(--sahi-green)]/15">
                              ₹{rate.toLocaleString()}
                            </span>
                          </div>
                        )}
                      </div>
                      {offeringDescription ? (
                        <div className="text-base text-[var(--sub-text-color)] leading-relaxed mt-0.5 max-h-24 sm:max-h-28 overflow-y-auto custom-scroll break-words whitespace-pre-line pr-1.5">
                          {offeringDescription}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {/* Requirement */}
                  <div className="space-y-1.5 text-left">
                    <Label className="text-sm font-medium text-[var(--text-color)]">
                      Requirement <span className="text-red-500 font-bold">*</span>
                    </Label>
                    <div className="relative">
                      <Textarea
                        id="desktop-modal-catalog-needs-input"
                        rows={3}
                        required
                        placeholder="e.g. quantity, specifications..."
                        value={offeringInquiryForm.needs}
                        onChange={(e) => updateFormField("needs", e.target.value)}
                        className="text-sm text-[var(--text-color)] placeholder:text-[var(--sub-text-color)] border-[var(--border)] focus:border-[var(--sahi-green)] focus:ring-1 focus:ring-[var(--sahi-green)] rounded-lg min-h-[90px] resize-y pr-14"
                      />
                      <SpeechControls
                        targetId="desktop-modal-catalog-needs-input"
                        value={offeringInquiryForm.needs}
                        onChange={(val) => updateFormField("needs", val)}
                        isMobile={isMobile}
                        className="absolute right-2 top-2"
                      />
                    </div>
                  </div>

                  {/* Contact Details */}
                  <InquiryContactFields
                    form={offeringInquiryForm}
                    onChange={updateFormField}
                    inputHeight="h-10"
                    rounded="rounded-lg"
                  />
                </div>

                <div className="p-4 flex flex-row items-center justify-start gap-4 shrink-0 bg-background">
                  <Button
                    type="submit"
                    variant="sahiGreen"
                    disabled={submittingOfferingInquiry}
                    className="px-5 h-10 font-semibold text-sm flex items-center justify-center gap-1.5 cursor-pointer rounded-lg shadow-xs"
                  >
                    {submittingOfferingInquiry ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" /> Submit Inquiry
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={submittingOfferingInquiry}
                    onClick={resetOfferingModalState}
                    className="sahi-secondary-button px-5 h-10 font-semibold text-sm cursor-pointer rounded-lg flex items-center justify-center gap-1.5"
                  >
                    <X className="h-4 w-4" />
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        );
      })()}
    </div>
  );
}

// Default export alias to PromoteBusiness for clean backward compatibility
export default function CommunitySidebar(props) {
  return <PromoteBusiness {...props} />;
}
